/* eslint-disable react/forbid-dom-props */
/**
 * ScandiPWA - Progressive Web App for Magento
 *
 * Copyright © Scandiweb, Inc. All rights reserved.
 * See LICENSE for license details.
 *
 * @license OSL-3.0 (Open Software License ("OSL") v. 3.0)
 * @package scandipwa/base-theme
 * @link https://github.com/scandipwa/base-theme
 */

import PropTypes from 'prop-types';
import React, { lazy, PureComponent, Suspense } from 'react';

import BrandDetails from '../../component/BrandDetails';
import { CATEGORY_FILTER_OVERLAY_ID } from 'Component/CategoryFilterOverlay/CategoryFilterOverlay.config';
import CategoryItemsCount from 'Component/CategoryItemsCount';
import CategoryProductList from 'Component/CategoryProductList';
import CategorySort from 'Component/CategorySort';
import ContentWrapper from 'Component/ContentWrapper';
import FilterIcon from 'Component/FilterIcon';
import GridIcon from 'Component/GridIcon';
import Html from 'Component/Html';
import ListIcon from 'Component/ListIcon';
import Loader from 'Component/Loader';
import { CategoryTreeType } from 'Type/Category';
import { FilterInputType, FilterType } from 'Type/ProductList';
import { isCrawler, isSSR } from 'Util/Browser';
import BrowserDatabase from 'Util/BrowserDatabase';
import { Range } from 'rc-slider';
const style = { width: 108 };
import 'rc-slider/assets/index.css';

import {
    DISPLAY_MODE_BOTH,
    DISPLAY_MODE_CMS_BLOCK,
    DISPLAY_MODE_PRODUCTS,
    GRID_LAYOUT,
    LAYOUT_KEY,
    LIST_LAYOUT
} from './BrandPage.config';

import './BrandPage.style';
import Link from 'Component/Link';
import { appendWithStoreCode } from 'Util/Url';
import media, { WYSIWYG_MEDIA } from 'Util/Media/Media';
import {BRAND_MEDIA, BRAND_MEDIA_MOBILE, BRAND_MEDIA_SMALL} from "../../util/Media";

export const CategoryFilterOverlay = lazy(() => import(
    /* webpackMode: "lazy", webpackChunkName: "overlays-category" */ 'Component/CategoryFilterOverlay'
    ));
export const UniversalIcon = lazy(() => import(
    /* webpackMode: "lazy", webpackChunkName: "icons" */
    'Component/UniversalIcon'
    ));

/** @namespace Route/BrandPage/Component */
export class BrandPage extends PureComponent {
    static propTypes = {
        brand: CategoryTreeType.isRequired,
        filters: PropTypes.objectOf(PropTypes.shape).isRequired,
        sortFields: PropTypes.shape({
            options: PropTypes.array
        }).isRequired,
        selectedSort: PropTypes.shape({
            sortDirection: PropTypes.oneOf([
                'ASC',
                'DESC'
            ]),
            sortKey: PropTypes.string
        }).isRequired,
        onSortChange: PropTypes.func.isRequired,
        toggleOverlayByKey: PropTypes.func.isRequired,
        selectedFilters: FilterType.isRequired,
        filter: FilterInputType.isRequired,
        search: PropTypes.string,
        isContentFiltered: PropTypes.bool,
        isCurrentCategoryLoaded: PropTypes.bool,
        isMatchingListFilter: PropTypes.bool,
        isMatchingInfoFilter: PropTypes.bool,
        totalPages: PropTypes.number,
        totalItems: PropTypes.number.isRequired,
        isMobile: PropTypes.bool.isRequired,
        onGridButtonClick: PropTypes.func.isRequired,
        onListButtonClick: PropTypes.func.isRequired,
        defaultPlpType: PropTypes.string,
        selectedLayoutType: PropTypes.string,
        plpTypes: PropTypes.arrayOf(PropTypes.string),
        appliedFiltersCount: PropTypes.number
    };

    static defaultProps = {
        isContentFiltered: true,
        isMatchingListFilter: false,
        isCurrentCategoryLoaded: false,
        isMatchingInfoFilter: false,
        totalPages: 1,
        defaultPlpType: '',
        plpTypes: [],
        search: '',
        appliedFiltersCount: 0,
        selectedLayoutType: ''
    };

    state = {
        activeLayoutColumns: 50,
        selectedCategoryItem : 0,
    };

    static getDerivedStateFromProps(props) {
        const {
            isMobile,
            defaultPlpType,
            selectedLayoutType
        } = props;

        /*
        * Use stored plpType from the BrowserDatabase
        * if there is one
        */
        const storedPlpType = BrowserDatabase.getItem(LAYOUT_KEY) || selectedLayoutType;

        if (storedPlpType) {
            const activeLayoutType = isMobile
                ? GRID_LAYOUT
                : storedPlpType || defaultPlpType;

            return { activeLayoutType };
        }

        const activeLayoutType = isMobile
            ? GRID_LAYOUT
            : selectedLayoutType || defaultPlpType;

        return { activeLayoutType };
    }

    selectCategoryItem (itemIndex) {
        if(this.state.selectedCategoryItem === itemIndex){
            this.setState({
                selectedCategoryItem : 0
            });
        } else {
            this.setState({
                selectedCategoryItem : itemIndex
            });
        }
    }

    setColumnsLayoutValue(value) {
        this.setState({ activeLayoutColumns: value[1] });
    }

    onFilterButtonClick = () => {
        const { toggleOverlayByKey } = this.props;
        toggleOverlayByKey(CATEGORY_FILTER_OVERLAY_ID);
    };

    displayProducts() {
        const {
            brand: {
                display_mode = DISPLAY_MODE_PRODUCTS
            } = {}
        } = this.props;

        return display_mode === null
            || display_mode === DISPLAY_MODE_PRODUCTS
            || display_mode === DISPLAY_MODE_BOTH;
    }

    displayCmsBlock() {
        const { brand: { display_mode } = {} } = this.props;

        return display_mode === DISPLAY_MODE_CMS_BLOCK
            || display_mode === DISPLAY_MODE_BOTH;
    }

    renderBrandDetails() {
        const {
            brand,
            isCurrentCategoryLoaded
        } = this.props;

        return (
            <BrandDetails
                brand={ brand }
                isCurrentCategoryLoaded={ isCurrentCategoryLoaded }
            />
        );
    }

    renderFiltersCount() {
        const { appliedFiltersCount } = this.props;

        if (!appliedFiltersCount) {
            return null;
        }

        return (
            <span block="BrandPage" elem="Subheading">
                { ` (${appliedFiltersCount})` }
            </span>
        );
    }

    renderFilterButton() {
        const {
            isContentFiltered, totalPages, brand: { is_anchor = true }
        } = this.props;

        if ((!isContentFiltered && totalPages === 0) || !is_anchor) {
            return null;
        }

        return (
            <button
                block="BrandPage"
                elem="Filter"
                onClick={ this.onFilterButtonClick }
            >
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <circle cx="3" cy="4" r="2" fill="black"/>
                    <circle cx="8" cy="10" r="2" fill="black"/>
                    <circle cx="13" cy="6" r="2" fill="black"/>
                    <path d="M2.99992 4V14" stroke="black" strokeWidth="0.7"/>
                    <path d="M8 3V14" stroke="black" strokeWidth="0.7"/>
                    <path d="M13 3L12.9998 14" stroke="black" strokeWidth="0.7"/>
                </svg>
                <span>{ __('Filters') }</span>
            </button>
        );
    }

    renderFilterPlaceholder() {
        return (
            <div block="BrandPage" elem="FilterPlaceholder">
                <Loader isLoading />
            </div>
        );
    }

    renderFilterOverlay(active_status) {
        const {
            filters :  { brand, ...filters },
            selectedFilters,
            isMatchingInfoFilter,
            isMobile
        } = this.props;

        const { brand: { is_anchor = true } } = this.props;

        if (!this.displayProducts()) {
            return null;
        }

        return (
            <Suspense fallback={ this.renderFilterPlaceholder() }>
                <CategoryFilterOverlay
                    availableFilters={ filters }
                    customFiltersValues={ selectedFilters }
                    isMatchingInfoFilter={ isMatchingInfoFilter }
                    activeStatus = { active_status }
                    isMobile={ isMobile }
                    isCategoryAnchor={ !!is_anchor }
                />
            </Suspense>
        );
    }

    renderCategorySort() {
        const {
            sortFields,
            selectedSort,
            onSortChange,
            isMatchingInfoFilter
        } = this.props;

        const { options = {} } = sortFields;
        const updatedSortFields = Object.values(options).map(({ value: id, label }) => ({ id, label }));
        const { sortDirection, sortKey } = selectedSort;

        return (
            <CategorySort
                isMatchingInfoFilter={ isMatchingInfoFilter }
                onSortChange={ onSortChange }
                sortFields={ updatedSortFields }
                sortKey={ sortKey }
                sortDirection={ sortDirection }
            />
        );
    }

    renderLayoutButton = (type) => {
        const {
            onGridButtonClick,
            onListButtonClick
        } = this.props;

        const { activeLayoutType } = this.state;

        switch (type) {
            case GRID_LAYOUT:
                return (
                    <button
                        key={ type }
                        onClick={ onGridButtonClick }
                        mix={ { block: GRID_LAYOUT, mods: { isActive: activeLayoutType === GRID_LAYOUT } } }
                        aria-label="grid"
                    >
                        <GridIcon isActive={ activeLayoutType === GRID_LAYOUT } />
                    </button>
                );
            case LIST_LAYOUT:
                return (
                    <button
                        key={ type }
                        onClick={ onListButtonClick }
                        mix={ { block: LIST_LAYOUT, mods: { isActive: activeLayoutType === LIST_LAYOUT } } }
                        aria-label="list"
                    >
                        <ListIcon isActive={ activeLayoutType === LIST_LAYOUT } />
                    </button>
                );
            default:
                return false;
        }
    };

    renderLayoutButtons() {
        const { plpTypes } = this.props;

        /*
        * So far there is only two types of
        * the Storefront list modes
         */
        if (plpTypes.length !== 2) {
            return null;
        }

        return (
            <div block="BrandPage" elem="LayoutButtons">
                { plpTypes.map(this.renderLayoutButton) }
            </div>
        );
    }

    renderItemsCount(isVisibleOnMobile = false) {
        const { isMatchingListFilter, isMobile, totalItems } = this.props;

        if ((isVisibleOnMobile && !isMobile) || (!isVisibleOnMobile && isMobile) || totalItems === 0) {
            return null;
        }

        return (
            <CategoryItemsCount
                isMatchingListFilter={ isMatchingListFilter }
            />
        );
    }

    renderCategoryProductList() {
        const {
            filter,
            search,
            selectedSort,
            selectedFilters,
            isMatchingListFilter,
            isCurrentCategoryLoaded,
            isMatchingInfoFilter,
            brand: { non_hero_grid_cms_block_content }
        } = this.props;

        const { activeLayoutType, activeLayoutColumns } = this.state;

        if (!this.displayProducts()) {
            return null;
        }

        return (
            <div
                block="BrandPage"
                elem="ProductListWrapper"
                mods={ { isPrerendered: isSSR() || isCrawler() } }
            >
                { this.renderItemsCount(true) }
                <CategoryProductList
                    filter={ filter }
                    search={ search }
                    sort={ selectedSort }
                    selectedFilters={ selectedFilters }
                    isCurrentCategoryLoaded={ isCurrentCategoryLoaded }
                    isMatchingListFilter={ isMatchingListFilter }
                    isMatchingInfoFilter={ isMatchingInfoFilter }
                    activeLayoutColumns={activeLayoutColumns}
                    layout={ activeLayoutType || GRID_LAYOUT }
                    categoryPageCmsBlockListing={non_hero_grid_cms_block_content}
                />
            </div>
        );
    }

    renderCmsBlock() {
        const { brand: { cms_block } } = this.props;

        if (!cms_block || !this.displayCmsBlock()) {
            return null;
        }

        const { content, disabled } = cms_block;

        if (disabled) {
            return null;
        }

        return (
            <div
                block="BrandPage"
                elem="CMS"
            >
                <Html content={ content } />
            </div>
        );
    }

    renderLayoutSlider() {
        const { isMobile } = this.props;
        const { selectedCategoryItem } = this.state;

        if(isMobile) {
            return (
                <div className="BrandPage-LayoutContentWrapper">
                    <div className="BrandPage-LayoutSliderButton" id="BrandPage-LayoutSliderButton" onClick={this.selectCategoryItem.bind(this,1) }>
                        <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <rect x="2" y="2" width="4" height="4" fill="black"/>
                            <rect x="8" y="2" width="4" height="4" fill="black"/>
                            <rect x="2" y="8" width="4" height="4" fill="black"/>
                            <rect x="8" y="8" width="4" height="4" fill="black"/>
                        </svg>
                        <span>{ __('Εμφάνιση') }</span>
                    </div>
                    <div className={ `FieldSelect-OptionsWrapper ${selectedCategoryItem === 1 ? "active" : ""} ` }>
                        <div className="FieldSelect-FieldLabelMobile">
                            <div className="FieldSelect-FieldLabel">{ __('Εμφάνιση') }</div>
                            <svg
                                block="CloseIcon"
                                width="24"
                                height="24"
                                viewBox="0 0 24 24"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                                onClick={this.selectCategoryItem.bind(this,0) }
                            >
                                <path d="M16.192 6.34399L11.949 10.586L7.707 6.34399L6.293 7.75799L10.535 12L6.293 16.242L7.707 17.656L11.949 13.414L16.192 17.656L17.606 16.242L13.364 12L17.606 7.75799L16.192 6.34399Z" />
                            </svg>
                        </div>
                        <div className="BrandPage-LayoutSliderWrapper">
                            <div className="BrandPage-LayoutSlider" style={style}>
                                <div className="BrandPage-LayoutSlider_label">{ __('Eμφάνιση') }</div>
                                <Range step={50} defaultValue={[0, 50]} onAfterChange={this.setColumnsLayoutValue.bind(this)} />
                            </div>
                        </div>
                    </div>
                </div>
            );
        }

        return (
            <div className="BrandPage-LayoutSlider" style={style}>
                <div className="BrandPage-LayoutSlider_label">{ __('Eμφάνιση') }</div>
                <Range step={50} defaultValue={[0, 50]} onAfterChange={this.setColumnsLayoutValue.bind(this)} />
            </div>
        );
    }

    renderMiscellaneous() {
        const { totalItems, isMobile } = this.props;
        const { selectedCategoryItem } = this.state;

        if (totalItems === 0 || !this.displayProducts()) {
            return <aside block="BrandPage" elem="Miscellaneous" mods={ { noResults: true } } />;
        }

        if(isMobile) {
            return (
                <aside block="BrandPage" elem="Miscellaneous" className={ selectedCategoryItem !== 0 ? 'active' : '' }>
                    <div
                        block="BrandPage"
                        elem="LayoutWrapper"
                        mods={ { isPrerendered: isSSR() || isCrawler() } }
                    >
                        { this.renderLayoutSlider() }
                        { this.renderCategorySort() }
                    </div>
                    { this.renderFilterButton() }
                </aside>
            );
        }

        return (
            <aside block="BrandPage" elem="Miscellaneous">
                <div
                    block="BrandPage"
                    elem="LayoutWrapper"
                    mods={ { isPrerendered: isSSR() || isCrawler() } }
                >
                    { this.renderLayoutSlider() }
                    { this.renderItemsCount(false) }
                    { this.renderCategorySort() }
                </div>
                { this.renderFilterButton() }
            </aside>
        );
    }

    renderSliderImage(){
        const { brand : { slider_image, title }} = this.props;

        if(slider_image === null) {
            return(
                <div className="BrandPage__slider_image">
                    <h1 className="BrandPage__title">{ title }</h1>
                </div>
            );
        }

        return(
            <div className="BrandPage__slider_image">
                <UniversalIcon src={media(slider_image,BRAND_MEDIA_SMALL)} alt="@@"/>
                <h1 className="BrandPage__title">{ title }</h1>
            </div>
        );
    }

    renderBanner(){
        const { brand : { image, image_mobile, layout_type, banner_link } } = this.props;

        if(image === null)
            return null;

        if(window.innerWidth < 461 && image_mobile) {
            return(
                <div className={"BrandPage__main_image "+layout_type}>
                    {
                        banner_link ?
                            <Link to={appendWithStoreCode(banner_link)}>
                                <UniversalIcon src={media(image_mobile,BRAND_MEDIA_MOBILE)} alt="@@"/>
                            </Link>
                            :
                            <UniversalIcon src={media(image_mobile,BRAND_MEDIA_MOBILE)} alt="@@"/>
                    }
                </div>
            );
        }

        return(
            <div className={"BrandPage__main_image "+layout_type}>
                {
                    banner_link ?
                        <Link to={appendWithStoreCode(banner_link)}>
                            <UniversalIcon src={media(image,BRAND_MEDIA)} alt="@@"/>
                        </Link>
                        :
                        <UniversalIcon src={media(image,BRAND_MEDIA)} alt="@@"/>
                }
            </div>
        );
    }

    renderDescription(){
        const { brand : { description } } = this.props;

        if(description === null)
            return null;

        return description ? <div className="BrandPage__main_description"><Html content={ description } /></div> : ''
    }

    renderContent() {
        const { isMobile } = this.props;

        return (
            <>
                { !isMobile ?
                    <div className="BrandPage-LeftCol">
                        { this.renderSliderImage() }
                        { this.renderFilterOverlay(true) }
                    </div>
                    :
                    <>
                        { this.renderSliderImage() }
                        { this.renderFilterOverlay(true) }
                    </>
                }
                {this.renderBanner()}
                {this.renderDescription()}
                {this.renderCmsBlock()}
                <div className="BrandPage-ContentSelectors">
                    {this.renderMiscellaneous()}
                    {this.renderFilterOverlay(false)}
                </div>
                {this.renderCategoryProductList()}
            </>
        );
    }

    render() {
        const hideProducts = !this.displayProducts();
        const { totalItems, isChanel } = this.props;

        return (
            <main block="BrandPage" mods={ { noResults: totalItems === 0 } } className={ isChanel ? "isChanel" : "" }>
                <ContentWrapper
                    wrapperMix={ {
                        block: 'BrandPage',
                        elem: 'Wrapper',
                        mods: { hideProducts }
                    } }
                    label="Category page"
                >
                    { this.renderContent() }
                </ContentWrapper>
            </main>
        );
    }
}

export default BrandPage;
